import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  usePortalReviewScenarios,
  usePortalSignInAdapter,
} from "@shared-core/data/PortalSignInAdapterContext";
import { setRejectedRequestHandler } from "@shared-core/data/api/providers";
import { decidePortalLanding } from "@shared-core/domain/portalSignIn";
import { useListQuery } from "@shared-core/domain/useListQuery";
import type { PortalLinkedCustomer } from "@shared-core/data/customer-portal-access-types";
import { landingPagePath } from "./outcomePath";

/**
 * What the landing gate decided before any portal page renders
 * (docs/features/customer-storefront/28_frontends_p4_update_spec.md section 5.2).
 */
export type PortalGate =
  | { status: "loading" }
  | { status: "error"; message: string; error: unknown; retry: () => void }
  /** Go to the page that shows this sign-in outcome (welcome, chooser, no access, blocked) or the sign-in page. */
  | { status: "redirect"; to: string }
  /** Go in. `customer` is who the person is working as (null: nothing to choose between). */
  | {
      status: "open";
      customer: PortalLinkedCustomer | null;
      personName: string | null;
      linked: PortalLinkedCustomer[];
    };

/** A 401 straight after the gate opened is not "your choice went stale": do not loop on it. */
const MIN_MS_BETWEEN_REOPENS = 1500;

function isChooseCustomerBody(body: unknown): boolean {
  const detail = (body as { detail?: { code?: unknown } } | null)?.detail;
  return typeof detail === "object" && detail?.code === "choose_customer";
}

/**
 * Reads what the server decided about the signed-in person and decides where they go: a page for
 * the outcome, the sign-in page when the token has lapsed, or into the portal as one customer. The
 * customer chosen in this browser session (which a refresh keeps) and a choice remembered on the
 * device are used silently while they are still one of their customers (`decidePortalLanding`).
 *
 * While the portal is open it also watches for the two answers that mean the choice is no longer
 * valid (a 401, or a 409 `choose_customer`): either pauses the choice and runs the gate again, which
 * sends a revoked person to the right page and a newly blocked customer to its not-available page.
 */
export function usePortalSignInGate(): PortalGate {
  const adapter = usePortalSignInAdapter();
  const review = usePortalReviewScenarios();
  const [searchParams] = useSearchParams();
  // Review tool, mock build only: `?scenario=` says which fixture person "signed in".
  const scenario = review
    ? (searchParams.get("scenario") ?? undefined)
    : undefined;
  const [attempt, setAttempt] = useState(0);
  const openedAt = useRef(0);
  // Who the person was working as when a refused request cleared the choice, so the next landing can say
  // that customer is not available instead of opening another one. Used once, by the re-run it triggers.
  const previousCustomerId = useRef<string | null>(null);

  const { state, retry } = useListQuery(async () => {
    if (review && scenario) review.selectScenario(scenario);
    let outcome;
    try {
      outcome = await adapter.getSignInOutcome();
    } catch (error) {
      // A lapsed sign-in ends the session's customer too: whoever signs in next starts with no choice.
      if ((error as { status?: unknown } | null)?.status === 401)
        adapter.clearChosenCustomer();
      throw error;
    }
    const rememberedId = adapter.getRememberedCustomerId();
    const landing = decidePortalLanding(outcome, {
      // A choice the server just refused is kept for the session only to be named if it is blocked:
      // it must not be opened again, or a refusal that is not about a block would loop.
      chosenCustomerId: previousCustomerId.current
        ? null
        : adapter.getChosenCustomerId(),
      rememberedCustomerId: rememberedId,
      welcomeAcknowledged: adapter.hasAcknowledgedWelcome(),
      previousCustomerId: previousCustomerId.current,
    });
    previousCustomerId.current = null;
    if (landing.kind === "open" && landing.customer) {
      // Works as this customer from the first request. A choice already remembered stays remembered.
      adapter.chooseCustomer(
        landing.customer.customer_id,
        rememberedId === landing.customer.customer_id,
      );
    }
    return { outcome, landing };
  }, [adapter, review, scenario, attempt]);

  const isOpen =
    state.status === "success" && state.data.landing.kind === "open";
  useEffect(() => {
    if (!isOpen) return;
    openedAt.current = performance.now();
    setRejectedRequestHandler(({ status, path, body }) => {
      if (path.startsWith("session/customers")) return;
      const choiceNoLongerValid =
        status === 401 || (status === 409 && isChooseCustomerBody(body));
      if (!choiceNoLongerValid) return;
      if (performance.now() - openedAt.current < MIN_MS_BETWEEN_REOPENS) return;
      previousCustomerId.current = adapter.getChosenCustomerId();
      adapter.pauseChosenCustomer();
      setAttempt((n) => n + 1);
    });
    return () => setRejectedRequestHandler(null);
  }, [adapter, isOpen]);

  if (state.status === "loading") return { status: "loading" };
  if (state.status === "error") {
    // A lapsed sign-in (the token is missing, expired or no longer resolves) is a 401: back to sign-in.
    if ((state.error as { status?: unknown } | null)?.status === 401)
      return { status: "redirect", to: paths.portalSignIn() };
    return {
      status: "error",
      message: state.message,
      error: state.error,
      retry,
    };
  }

  const { outcome, landing } = state.data;
  if (landing.kind === "page") {
    return {
      status: "redirect",
      to: landingPagePath(landing.page, scenario, landing.blockedCustomerId),
    };
  }
  return {
    status: "open",
    customer: landing.customer,
    personName: outcome.kind === "no_access" ? null : outcome.person_name,
    linked: outcome.kind === "no_access" ? [] : outcome.customers,
  };
}
